import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

// Relative-time formatting — finance reads "2 days ago" much faster than a raw timestamp,
// but the absolute ISO time stays accessible as a hover-tooltip so an auditor can pin the
// exact moment if they need it for a report.
export const relativeTime = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  return new Date(iso).toISOString().slice(0, 10);
};

// Resolve a raw project id to its friendly name when the labels map has one. Falls back to
// the raw value so the diff still renders something the auditor can correlate (e.g. when
// the project has been deleted from the cost base since the change was recorded).
const labelFor = (value, labels) => (labels && labels[value]) || value;

// Friendly names for refinement dimensions — same map the picker uses. Kept local so the
// audit log isn't coupled to the editor's component tree; if a new dim appears here before
// it's in the picker, fall through to the raw dim id (still better than blank).
const DIM_LABEL = {
  label: "Label",
  sku_category: "SKU category",
  service: "Service",
};

// "tag \"environment\"" reads better than "Label key=environment" for finance, and matches
// what the picker shows. For non-label dims the key isn't meaningful.
const refinementLabel = (r) => {
  if (r.dim === "label" && r.key) return `tag "${r.key}"`;
  return DIM_LABEL[r.dim] || r.dim || "refinement";
};

// Refinements are identified by (dim, key) — that's how the picker prevents duplicates and
// how the source key is built. Same identity here so we can detect *modification* (same
// dim+key, different values) rather than reporting it as a remove-then-add.
const refSig = (r) => `${r.dim || ""}|${r.key || ""}`;

const arraysEqual = (a, b) => {
  const sa = [...(a || [])].sort();
  const sb = [...(b || [])].sort();
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
};

const fmtValues = (vs) => (vs && vs.length ? vs.join(", ") : "(no values)");

// Pulls the source-row fields most likely to have changed and renders a *list* of diff
// lines. Computed client-side from the snapshot JSON so the wire shape doesn't lock us in.
// Returns an array of strings (one per change) — empty when nothing semantically changed.
const summariseDiff = (prev, next, labels) => {
  if (!prev && next) return ["Initial state recorded"];
  if (prev && !next) return ["Source archived"];
  if (!prev || !next) return [];
  const lines = [];
  if (prev.display_name !== next.display_name) {
    lines.push(`Name: "${prev.display_name}" → "${next.display_name}"`);
  }
  if (prev.source_type_id !== next.source_type_id) {
    lines.push(`Type: ${prev.source_type_id || "—"} → ${next.source_type_id || "—"}`);
  }
  // Project scope — set diff so swaps surface even when count stays equal.
  const prevProjects = prev.metadata?.criteria?.scope?.projects || [];
  const nextProjects = next.metadata?.criteria?.scope?.projects || [];
  const addedProjects = nextProjects.filter((p) => !prevProjects.includes(p));
  const removedProjects = prevProjects.filter((p) => !nextProjects.includes(p));
  if (addedProjects.length) {
    lines.push(`+ project${addedProjects.length === 1 ? "" : "s"} ${addedProjects.map((p) => labelFor(p, labels)).join(", ")}`);
  }
  if (removedProjects.length) {
    lines.push(`− project${removedProjects.length === 1 ? "" : "s"} ${removedProjects.map((p) => labelFor(p, labels)).join(", ")}`);
  }
  // Refinement-level diff. Compare by (dim, key) signature — counting alone misses three
  // very common edits: (1) values changed within an existing refinement (e.g. SKU category
  // {Compute} → {Compute, Storage}), (2) one refinement swapped for another at the same
  // total count, (3) any modification that nets to the same length. All previously showed
  // up as "Re-saved with no field changes", which read to finance as "the save was lost".
  const prevRefs = prev.metadata?.criteria?.refinements || [];
  const nextRefs = next.metadata?.criteria?.refinements || [];
  const prevRefMap = new Map(prevRefs.map((r) => [refSig(r), r]));
  const nextRefMap = new Map(nextRefs.map((r) => [refSig(r), r]));
  for (const [sig, r] of nextRefMap) {
    if (!prevRefMap.has(sig)) {
      lines.push(`+ ${refinementLabel(r)}: ${fmtValues(r.values)}`);
    }
  }
  for (const [sig, r] of prevRefMap) {
    if (!nextRefMap.has(sig)) {
      lines.push(`− ${refinementLabel(r)}: ${fmtValues(r.values)}`);
    }
  }
  for (const [sig, r] of nextRefMap) {
    const prevR = prevRefMap.get(sig);
    if (prevR && !arraysEqual(prevR.values, r.values)) {
      lines.push(`${refinementLabel(r)}: ${fmtValues(prevR.values)} → ${fmtValues(r.values)}`);
    }
  }
  // Deliberately suppress match_kind churn. Transitions like 'classified' → 'criteria' are
  // an internal storage-representation move from the scope-funnel rewrite — they look like
  // engineering bug-reports to a finance auditor and carry no information about what the
  // source actually captures. If the captured rows change, the project/refinement diffs
  // above already say so.
  return lines;
};

// Snapshots arrive parsed from authenticatedFetch — Postgres JSONB round-trips through
// json.RawMessage as a plain object/array, so we just verify the shape here.
const asObj = (v) => (v && typeof v === "object" ? v : null);

const CHANGE_META = {
  CREATE:  { label: "Created",  badge: "bg-success-soft text-success",   icon: icon({ name: "plus",         style: "solid" }) },
  UPDATE:  { label: "Updated",  badge: "bg-surface-2 text-text-2",       icon: icon({ name: "pen",          style: "solid" }) },
  ARCHIVE: { label: "Archived", badge: "bg-warning-soft text-warning",   icon: icon({ name: "box-archive",  style: "solid" }) },
  RESTORE: { label: "Restored", badge: "bg-success-soft text-success",   icon: icon({ name: "rotate-left",  style: "solid" }) },
};

// Pure timeline renderer — takes the entries array and renders the vertical list. No data
// fetching, no chrome (card / drawer / inline). Reused by both the drawer body and the
// always-loaded inline-summary path so the diff/action/time formatting stays consistent.
//
// `valueLabels` maps a raw facet value (today: project id) → friendly name. Threaded down
// from DefineSourcePage which already builds it for the picker/preview, so the audit log
// renders "InstantViewAI" rather than the project UUID without a second round-trip.
const SourceHistoryTimeline = ({ entries, valueLabels }) => {
  if (!entries.length) {
    return <p className="text-xs text-text-2 italic">No recorded changes yet.</p>;
  }
  return (
    <ol className="space-y-5">
      {entries.map((e) => {
        const prev = asObj(e.previous_values);
        const next = asObj(e.new_values);
        const lines = summariseDiff(prev, next, valueLabels);
        const meta = CHANGE_META[e.change_type] || { label: e.change_type, badge: "bg-surface-2 text-text-2", icon: null };
        const actor = e.actor_display || e.changed_by;
        return (
          <li key={e.id} className="flex items-start gap-3">
            {/* Icon-augmented action badge. Icon + label reads faster than text-only and
                carries the meaning even when the badge is glanced at the corner of the eye. */}
            <span className={`flex-none mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide rounded-full px-1.5 py-0.5 ${meta.badge}`}>
              {meta.icon && <FontAwesomeIcon icon={meta.icon} className="text-[9px]" />}
              {meta.label}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-xs text-text truncate" title={actor || "unknown actor"}>
                  {actor ? actor : <span className="italic text-text-2">unknown actor</span>}
                </p>
                <p className="text-[11px] text-text-3 flex-none" title={e.changed_at}>
                  {relativeTime(e.changed_at)}
                </p>
              </div>
              {lines.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {lines.map((line, i) => (
                    <li key={i} className="text-[11px] text-text-2 break-words leading-snug">
                      {line}
                    </li>
                  ))}
                </ul>
              )}
              {lines.length === 0 && e.change_type === "UPDATE" && (
                <p className="mt-1 text-[11px] text-text-3 italic">Re-saved with no field changes</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

SourceHistoryTimeline.propTypes = {
  entries: PropTypes.array.isRequired,
  // Friendly-name map for raw facet values (e.g. project id → "InstantViewAI"). Optional;
  // when absent the diff falls back to raw values so the timeline is still readable.
  valueLabels: PropTypes.object,
};

export default SourceHistoryTimeline;
